import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { of, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from '../services/user.service';
import {
  SharedActions,
  UserActions,
  UserSettingsActions,
} from './user-account-settings.actions';
import { UserAccountSettingsSelectors } from './user-account-settings.selector-types';
import { UserAccountSettingsState } from './user-account-settings.reducer';
import { ACCESS_TOKEN_KEY } from '../consts/local-storage.const';
import { UserSettingsModels } from '@share-models';
import { UserSettingsService } from '../services/user-settings.service';
import { errMessage, LocalStorageService, MsgBoxType, ToastService } from '@ui';

export const loadUserProfile$ = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    localStorage = inject(LocalStorageService),
  ) =>
    actions$.pipe(
      ofType(UserActions.loadUserProfile),
      switchMap(() => {
        const accessToken = localStorage.getItemWithCheck(ACCESS_TOKEN_KEY);
        if (!accessToken) {
          return of(
            UserActions.loadUserProfileFail({ message: 'No access token' }),
          );
        }

        return userService.me(accessToken).pipe(
          map((user) => UserActions.loadUserProfileSuccess({ payload: user })),
          catchError((err: HttpErrorResponse | Error) => {
            const message = errMessage(err);
            return of(
              UserActions.createOrGetUserProfileFail({ message }),
              UserActions.loadUserProfileFail({ message }),
            );
          }),
        );
      }),
    ),
  { functional: true },
);

export const loadUserSettings$ = createEffect(
  (
    actions$ = inject(Actions),
    userSettingsService = inject(UserSettingsService),
  ) =>
    actions$.pipe(
      ofType(UserSettingsActions.loadUserSettings),
      switchMap(() =>
        userSettingsService.me().pipe(
          map((settings) => UserSettingsActions.setUserSettings({ settings })),
          catchError((err: HttpErrorResponse | Error) => {
            const message = errMessage(err);
            return of(UserSettingsActions.loadUserSettingsFail({ message }));
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startUpdateUserProfile$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserActions.startUpdateUserProfile),
      switchMap((action) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserActions.updateUserProfile(action),
      ]),
    ),
  { functional: true },
);

export const updateUserProfile$ = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserActions.updateUserProfile),
      concatMap((action) =>
        userService.updateUserProfile(action.data).pipe(
          switchMap((res) => [
            UserActions.updateUserProfileSuccess({ payload: res }),
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translateService.instant('SUAS.TOASTS.PROFILE_UPDATED'),
            }),
          ]),
          catchError((err: HttpErrorResponse) =>
            of(
              SharedActions.setIsSaving({ isSaving: false }),
              UserActions.updateUserProfileFail({
                message: errMessage(err),
              }),
              SharedActions.showMessage({
                error: errMessage(err),
              }),
            ),
          ),
        ),
      ),
      catchError((err: HttpErrorResponse) =>
        of(
          SharedActions.setIsSaving({ isSaving: false }),
          UserActions.updateUserProfileFail({
            message: errMessage(err),
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startUpdateUserSettingsProcess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserSettingsActions.startUpdateUserSettingsProcess),
      switchMap(({ userSettings }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserSettingsActions.updateUserSettingsRequest({ userSettings }),
      ]),
    ),
  { functional: true },
);

export const updateUserSettingsRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<UserAccountSettingsState>),
    userSettingsService = inject(UserSettingsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserSettingsActions.updateUserSettingsRequest),
      withLatestFrom(
        store.select(UserAccountSettingsSelectors.selectUserSettings),
      ),
      switchMap(([{ userSettings }, currentSettings]) => {
        if (!currentSettings) {
          return of(
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              error: translateService.instant('SUAS.TOASTS.SETTINGS_MISSING'),
            }),
          );
        }

        const mergedSettings: UserSettingsModels.UserSettingsDocument = {
          ...currentSettings,
          ...userSettings,
        };

        const request$ = mergedSettings._id
          ? userSettingsService.update(mergedSettings)
          : userSettingsService.create(mergedSettings);

        return request$.pipe(
          switchMap((settings) => [
            UserSettingsActions.setUserSettings({ settings }),
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translateService.instant('SUAS.TOASTS.SETTINGS_UPDATED'),
            }),
          ]),
          catchError((err: HttpErrorResponse) => {
            const message = errMessage(err);
            return of(
              SharedActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({ error: message }),
            );
          }),
        );
      }),
    ),
  { functional: true },
);

export const startChangeEmailProcess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserActions.startChangeEmailProcess),
      switchMap(({ email }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserActions.changeEmailRequest({ email }),
      ]),
    ),
  { functional: true },
);

export const changeEmailRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserActions.changeEmailRequest),
      switchMap(({ email }) =>
        userService.requestEmailChange(email).pipe(
          switchMap((res) => {
            const id = res?._id ?? res?.id ?? '';
            return [
              SharedActions.setIsSaving({ isSaving: false }),
              UserActions.changeEmailRequestSuccess({ id }),
              SharedActions.showMessage({
                success: String(
                  translateService.instant('SUAS.TOASTS.ENTITY_SENT_TO_EMAIL', {
                    entity: 'Confirmation code',
                  }),
                ),
              }),
            ];
          }),
          catchError((err: HttpErrorResponse) => {
            const errorMessage = errMessage(err);
            return of(
              SharedActions.setIsSaving({ isSaving: false }),
              UserActions.changeEmailRequestFail({ error: errorMessage }),
              SharedActions.showMessage({ error: errorMessage }),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startChangePhoneProcess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserActions.startChangePhoneProcess),
      switchMap(({ phone }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserActions.changePhoneRequest({ phone }),
      ]),
    ),
  { functional: true },
);

export const changePhoneProcess$ = createEffect(
  (
    actions$ = inject(Actions),
    userSettingsService = inject(UserSettingsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserActions.changePhoneRequest),
      switchMap(({ phone }) =>
        userSettingsService.verifyPhone(phone).pipe(
          switchMap((res: { _id?: string; id?: string }) => [
            UserActions.changePhoneRequestSuccess({
              id: res?._id ?? res?.id ?? '',
            }),
            SharedActions.showMessage({ success: translateService.instant('SUAS.TOASTS.SMS_CODE_SENT') }),
            SharedActions.setIsSaving({ isSaving: false }),
          ]),
          catchError((err: HttpErrorResponse) => {
            const errorMessage = errMessage(err);
            return of(
              UserActions.changePhoneRequestFail({ error: errorMessage }),
              SharedActions.showMessage({ error: errorMessage }),
              SharedActions.setIsSaving({ isSaving: false }),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startConfirmCode$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserSettingsActions.startConfirmCode),
      switchMap(({ code, id }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserSettingsActions.confirmCode({ code, id }),
      ]),
    ),
  { functional: true },
);

export const confirmCode$ = createEffect(
  (
    actions$ = inject(Actions),
    userSettingsService = inject(UserSettingsService),
  ) =>
    actions$.pipe(
      ofType(UserSettingsActions.confirmCode),
      switchMap(({ code, id }) =>
        userSettingsService
          .sendCode({ confirmationCode: code, verificationId: id })
          .pipe(
            switchMap(() => [
              SharedActions.setIsSaving({ isSaving: false }),
              UserSettingsActions.confirmCodeSuccess(),
            ]),
            catchError((err: HttpErrorResponse) => {
              const errorMessage = errMessage(err);
              return of(
                SharedActions.setIsSaving({ isSaving: false }),
                SharedActions.showMessage({ error: errorMessage }),
                UserSettingsActions.confirmCodeFail(),
              );
            }),
          ),
      ),
    ),
  { functional: true },
);

export const updateDevicePageView$ = createEffect(
  (
    actions$ = inject(Actions),
    userSettingsService = inject(UserSettingsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserSettingsActions.updateDevicePageView),
      switchMap(({ newDevicePageView }) =>
        userSettingsService.setDevicePageView({ newDevicePageView }).pipe(
          switchMap((settings) => [
            UserSettingsActions.updateDevicePageViewSuccess({ settings }),
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: String(
                translateService.instant(
                  'SUAS.TOASTS.DEVICE_PAGE_VIEW_UPDATE_SUCCESS',
                ),
              ),
            }),
          ]),
          catchError((err: HttpErrorResponse) => {
            const errorMessage = errMessage(err);
            return of(
              SharedActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({ error: errorMessage }),
              UserSettingsActions.updateDevicePageViewFail(),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startUpdateTheme$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserSettingsActions.startUpdateTheme),
      switchMap(({ theme }: { theme: string }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserSettingsActions.updateThemeRequest({ theme: theme }),
      ]),
    ),
  { functional: true },
);

export const updateThemeRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    userSettingsService = inject(UserSettingsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserSettingsActions.updateThemeRequest),
      switchMap(({ theme }) =>
        userSettingsService.setTheme(theme).pipe(
          switchMap((settings) => [
            UserSettingsActions.setUserSettings({ settings }),
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({ success: translateService.instant('SUAS.TOASTS.THEME_UPDATED') }),
          ]),
          catchError((err: HttpErrorResponse) => {
            const message = errMessage(err);
            return of(
              SharedActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({ error: message }),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startUpdateWeeklySubscription$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserSettingsActions.startUpdateWeeklySubscription),
      switchMap(({ subForWeekly }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserSettingsActions.updateWeeklySubscriptionRequest({
          subForWeekly,
        }),
      ]),
    ),
  { functional: true },
);

export const updateWeeklySubscriptionRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    userSettingsService = inject(UserSettingsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserSettingsActions.updateWeeklySubscriptionRequest),
      switchMap(({ subForWeekly }: { subForWeekly: boolean }) =>
        userSettingsService.setWeeklySubscription(subForWeekly).pipe(
          switchMap((settings) => [
            UserSettingsActions.setUserSettings({ settings }),
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translateService.instant('SUAS.TOASTS.WEEKLY_SUBSCRIPTION_UPDATED'),
            }),
          ]),
          catchError((err: HttpErrorResponse) => {
            const message = errMessage(err);
            return of(
              SharedActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({ error: message }),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startUpdateGlobalSearchSettings$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserSettingsActions.startUpdateGlobalSearchSettings),
      switchMap(({ globalSearchSettings }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserSettingsActions.updateGlobalSearchSettingsRequest({
          globalSearchSettings,
        }),
      ]),
    ),
  { functional: true },
);

export const updateGlobalSearchSettingsRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    userSettingsService = inject(UserSettingsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserSettingsActions.updateGlobalSearchSettingsRequest),
      switchMap(({ globalSearchSettings }) =>
        userSettingsService.setGlobalSearchSettings(globalSearchSettings).pipe(
          switchMap((settings) => [
            UserSettingsActions.setUserSettings({ settings }),
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translateService.instant('SUAS.TOASTS.GLOBAL_SEARCH_SETTINGS_UPDATED'),
            }),
          ]),
          catchError((err: HttpErrorResponse) => {
            const message = errMessage(err);
            return of(
              SharedActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({ error: message }),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const startUpdateLanguage$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserSettingsActions.startUpdateLanguage),
      switchMap(({ lang }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserSettingsActions.updateLanguageRequest({ lang }),
      ]),
    ),
  { functional: true },
);

export const updateLanguageRequest$ = createEffect(
  (
    actions$ = inject(Actions),
    userSettingsService = inject(UserSettingsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(UserSettingsActions.updateLanguageRequest),
      switchMap(({ lang }) =>
        userSettingsService.setLang({ lang }).pipe(
          switchMap((settings) => [
            UserSettingsActions.setUserSettings({ settings }),
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: String(
                translateService.instant('SUAS.TOASTS.LANGUAGE_UPDATE_SUCCESS'),
              ),
            }),
          ]),
          catchError((err: HttpErrorResponse) => {
            const message = errMessage(err);
            return of(
              SharedActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({ error: message }),
            );
          }),
        ),
      ),
    ),
  { functional: true },
);

export const showMessage$ = createEffect(
  () => {
    const actions$ = inject(Actions);
    const toastService = inject(ToastService);

    return actions$.pipe(
      ofType(SharedActions.showMessage),
      tap(({ error, warning, success, info }) => {
        if (error) toastService.showCustomAlert(error, MsgBoxType.ERROR);
        if (warning) toastService.showCustomAlert(warning, MsgBoxType.WARNING);
        if (success) toastService.showCustomAlert(success, MsgBoxType.SUCCESS);
        if (info) toastService.showCustomAlert(info, MsgBoxType.INFO);
      }),
    );
  },
  { functional: true, dispatch: false },
);
